import { Spinner } from "@fluentui/react-components";
import { TeamsFxContext } from "../Context";
import ProblemReportCard from "./ProblemReportCard";
import { useState, useContext, useEffect, useRef } from "react";
import TabHeader from "../common/TabHeader";
import { Attachment } from "../../types";
import ProblemReportsToolbar, { filterProblemReportStatus } from "./ProblemReportsToolbar";
import { logger } from "../../Logger";
import { AttachedItem } from "../../types/ProblemType"
import * as microsoftTeams from '@microsoft/teams-js';
import { Constants } from "../common/Constants";
import { ListObjectRequestData } from "../../types/CommonTypes";
import { useListObjects } from "../../hooks/useListObjects";
import { ModelObject } from "../common/TcSOATypes";
import { AnalyticsManager } from "../../services/analyticsUtility";
import AnalyticsConstants from "../common/AnalyticsConstants.json";

const ProblemReportsList: React.FunctionComponent = () => {
  // Tracking problem reports tab visit
  useEffect( () => {
    async function startProcessing() {
      if (teamsContext.configuration) { 
        const analyticsManager = AnalyticsManager.getInstance();
        analyticsManager.logPlace(AnalyticsConstants.tabMyWorkspace);
      }
    }
    startProcessing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const teamsContext = useContext(TeamsFxContext);

  useEffect(() => {
    async function clearOnLogout() {
      if (!teamsContext?.teamcenter.isAuthenticated) {
        problemReportList.current = [];
        endReached.current = false;
        endIndex.current = 0;
        setStartIndex(0);
      }
    }
    clearOnLogout();
  }, [teamsContext?.teamcenter.isAuthenticated]);
  
  const i18n = teamsContext.i18n;
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const [filterStatus, setFilterStatus] = useState<string>(filterProblemReportStatus.all.value);
  const [startIndex, setStartIndex] = useState<number>(0);
  const [showSpinner, setShowSpinner] = useState<boolean>(false); 

  const [problems, setProblems] = useState<ModelObject[]>();
  const listObjectInput: ListObjectRequestData = {
    ObjectType: Constants.problemReport,
    SearchCriteria: {
      Status: filterStatus,
      searchString: searchQuery,
    },
    MaxToLoad: 50,
    StartIndex: startIndex
  };
  const problemsApi = useListObjects(listObjectInput);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const scrollPosition = useRef<number>(0);
  const problemReportList = useRef<any[]>([]);
  const endIndex = useRef<number>(0);
  const endReached = useRef<boolean>(false);
  
  const disableButton = () => {
    return (
      !teamsContext.teamcenter.isAuthenticated ||
      teamsContext.teamcenter.isAuthenticating ||
      !problemsApi ||
      problemsApi.isFetching
    );
  };

  const clearList = () => {
    setStartIndex(0);
    problemReportList.current = [];
  };

  const problemsLoaded = () => {
    logger.logTrace("Entered problemsLoaded");
    logger.logTrace("Exit problemsLoaded");
    return (
      problemsApi &&
      !problemsApi.isRefetching &&
      !problemsApi.isError &&
      problemsApi.isFetched
    );
  };

  const noProblemsLoaded = () => {
    logger.logTrace("Entered noProblemsLoaded");
    logger.logTrace("Exit noProblemsLoaded");
    return (
      problemsApi &&
      !problemsApi.isError &&
      problemsApi.isFetched &&
      problems &&
      !problems.length
    );
  };

  const onSearchChanged = (query: string) => {
    logger.logTrace("Entered onSearchChanged");
    clearList();
    setSearchQuery(query ? query : "");
    logger.logTrace("Exit onSearchChanged");
  };

  const onFilterStatusChanged = (status: string) => {
    logger.logTrace("Entered onFilterStatusChanged");
    clearList();
    setFilterStatus(status);
    logger.logTrace("Exit onFilterStatusChanged");
  };

  const onRefreshButtonClicked = () => {
    clearList();
    setShowSpinner(true);
    logger.logTrace("Entered onRefreshButtonClicked");
    problemsApi?.refetch();
    logger.logTrace("Exit onRefreshButtonClicked");
    setShowSpinner(false);
  };

  const refetchProblems = useRef(() => {
    logger.logTrace("Entered refetchProblems")
    problemsApi.refetch();
    logger.logTrace("Exit refetchProblems")
  });

  const loadMoreProblems = useRef(() => {
    logger.logTrace("Entered loadMoreProblems")
    problemsApi.refetch();
    logger.logTrace("Exit loadMoreProblems")
  });

  // Open in Teamcenter button should be enabled only on desktop
  function isOpenInTcButtonEnabled(): boolean {
    return (
      teamsContext.clientType !== microsoftTeams.HostClientType.ios &&
      teamsContext.clientType !== microsoftTeams.HostClientType.android
    );
  }


  const showProblemReportList = () => {
    const openInTcButtonEnabled: boolean = isOpenInTcButtonEnabled();
    if (showSpinner){
      return <Spinner appearance="inverted" />;
    }
    if (problemsApi && (problemsApi.isLoading || problemsApi.isRefetching)){
      if(problemReportList.current.length === 0) {
        return <Spinner appearance="inverted" />;
      } else {
        return (problemReportList.current);
      }
    }

    else if (problemsLoaded() && problemsApi.listItems) {
      const responseData = problemsApi.listItems;
      if ('searchResults' in responseData && 'teamcenterUri' in responseData  && 'modelObjects' in responseData && 'cursor' in responseData) {
        const searchResults: any[] = (responseData.searchResults as any[]) || [];
        const modelObjects: any[] = (responseData.modelObjects as any[]) || [];
        const teamcenterUri = responseData.teamcenterUri;
        const cursor: any = (responseData.cursor as any) || {};
        if(cursor){
          endIndex.current = cursor.endIndex;
          endReached.current = cursor.endReached;
        }

        searchResults.forEach((problem: any) => {
          if (problem !== null && problem !== undefined) {    
            const attachments: Attachment[] = [];
            let problemItem: AttachedItem = {};
            let impactedItem: AttachedItem = {};
            if (problem.props?.CMHasProblemItem?.dbValues){
              const targetProblemItem: any = modelObjects.find(
                (object: any) =>
                  object.uid ===
                    problem.props?.CMHasProblemItem?.dbValues[0]
              );
              if (targetProblemItem) {
                problemItem = {
                  id: targetProblemItem?.props?.item_id?.uiValues[0] ?? null,
                  uid: targetProblemItem?.uid ?? null,
                  name: targetProblemItem?.props?.object_name?.uiValues[0] ?? null,
                  imageTicket: targetProblemItem?.props?.awp0ThumbnailImageTicket?.uiValues[0] ?? null,
                };
              }
            }
            else if (problem.props?.CMHasImpactedItem?.dbValues){
              const targetImpactedItem: any = modelObjects.find(
                (object: any) =>
                  object.uid ===
                    problem.props?.CMHasImpactedItem?.dbValues[0]
              );
              if (targetImpactedItem) {
                impactedItem = {
                  id: targetImpactedItem?.props?.item_id?.uiValues[0] ?? null,
                  uid: targetImpactedItem?.uid ?? null,
                  name: targetImpactedItem?.props?.object_name?.uiValues[0] ?? null,
                  imageTicket: targetImpactedItem?.props?.awp0ThumbnailImageTicket?.uiValues[0] ?? null,
                };
              }
            }
    
            if (problem.props?.CMReferences?.dbValues){
              problem.props.CMReferences.dbValues.forEach((dbValue: string) => {
                const targetAttachment: any = modelObjects.find(
                  (object: any) =>
                    object.uid ===
                    dbValue
                );
                if (targetAttachment) {
                  const attachment: Attachment = {
                    uid: targetAttachment?.uid ?? null,
                    name: targetAttachment?.props?.object_name?.uiValues[0] ?? null,
                    imageTicket: targetAttachment?.props?.awp0ThumbnailImageTicket?.uiValues[0] ?? null,
                  };
                  attachments.push(attachment);
                }
            });
          }
          problem.problemItem = problemItem;
          problem.impactedItem = impactedItem;
          problem.value = problem.props?.object_type?.uiValues[0];
          problem.attachment = attachments;
          problem.openInTeamcenterUri = teamcenterUri ? teamcenterUri + `?uid=` + problem.uid : null;
        }
        });
          
        const problemReports: any[] = searchResults;
        
        var problemList = problemReports.map((problem) => (
          <ProblemReportCard
            key={problem?.uid}
            problem={problem}
            isOpenInTcButtonEnabled={openInTcButtonEnabled}
          />
        ));
        if(cursor.startIndex === 0){
          problemReportList.current = problemList;
        }
        else if(problemReportList.current.length < (endIndex.current + 1)){
          problemReportList.current = [...problemReportList.current,...problemList];
          window.scrollTo(0, scrollPosition.current);
        }
        return problemReportList.current;
      }
    } else if (noProblemsLoaded()) {
      return (
        <div className="no-result-container">
          <p>{i18n.NoResultsFoundText}</p>
        </div>
      );
    }
  };

  const handleScroll = () => {
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;

      // Check if the scroll position is at the bottom of the content
      if (scrollHeight - scrollTop - clientHeight < 20) {
        // Perform action when the scroll bar hits the bottom
        if(!endReached.current){
          scrollPosition.current = window.scrollY;
          setStartIndex( endIndex.current + 1);
        }
      }
    }
  };

  // Initalize teams app and notify success of loading
  useEffect(() => {
    (async () => {
      await microsoftTeams.app.initialize();
      microsoftTeams.app.notifySuccess();
    })();
  }, []);

  // Once authenticated and problems are loaded based on synopsis, filter the problems based on the filter status
  useEffect(() => {
    if (problemsApi.listItems  && problemsApi.isFetched) {
      setProblems( problemsApi.listItems );
    }
  }, [
    problemsApi.listItems,
    problemsApi.isFetched,
    problemsApi.isRefetching,
  ]);

  useEffect(() => {
    loadMoreProblems.current();
  }, [startIndex]);

  useEffect(() => {
    const element = contentRef.current;
    if (element) {
      element.addEventListener('scroll', handleScroll);
      return () => {
        element.removeEventListener('scroll', handleScroll);
      }
    }
  }, [teamsContext?.teamcenter.isAuthenticated]);

  useEffect(() => {
    refetchProblems.current();
  }, [filterStatus]);

  useEffect(() => {
    const container = contentRef.current;
    // Check if the scroll bar is enabled after rendering
    const checkScrollBar = () => {
      if (container && !endReached.current) {
        if ((container.scrollHeight <= container.clientHeight)) {
          setStartIndex(endIndex.current + 1);
      
          // Call the function again after a delay (e.g., 5 seconds)
          setTimeout(checkScrollBar, 5000);
        }
      }
    };
    // Initial check after rendering
    checkScrollBar();
    // Listen for changes and update accordingly (e.g., window resize)
    const handleResize = () => {
      checkScrollBar();
    };

    window.addEventListener('resize', handleResize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []); // Empty dependency array to run the effect only once after the initial render

  
  return (
    <>
      {teamsContext.teamcenter.isAuthenticated && (
        <div className="problem-report-list">
          <div className="problem-report-toolbar">
          <ProblemReportsToolbar
            onSearchChanged={onSearchChanged}
            onFilterStatusChanged={onFilterStatusChanged}
            filterStatus={filterStatus}
            disableButton={disableButton}
            onRefreshButtonClicked={onRefreshButtonClicked}
          />
          </div>
          <div className="problem-list-banner">
            <TabHeader/>
          </div>
          <div className="problem-list"
            ref={contentRef}
          >{showProblemReportList()}</div>
        </div>
      )}
    </>
  );
};

export default ProblemReportsList;
